import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, getDocs, onSnapshot,
  query, orderBy, collectionGroup,
} from 'firebase/firestore';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };

const RARITY = { Legendary: '#f59e0b', Epic: '#a78bfa', Rare: '#06b6d4', Common: '#64748b' };
const RARITY_ORDER = { Legendary: 4, Epic: 3, Rare: 2, Common: 1 };

const S = {
  bg: '#070c1a', panel: '#0d1526', input: '#060b18',
  border: 'rgba(255,255,255,0.08)', faint: 'rgba(255,255,255,0.04)',
  text: '#d9e2ff', muted: '#bdc8ce', dim: '#64748b',
  primary: '#65d4fa', green: '#34d399', red: '#f87171',
  mono: "'Share Tech Mono','Courier New',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

function relTime(ts) {
  if (!ts?.toDate) return '—';
  const d = ts.toDate();
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AchievementsPage({ userId, userName }) {
  const [tab, setTab] = useState('mine');
  const [definitions, setDefinitions] = useState([]);
  const [earned, setEarned] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const db = DB();
    if (!db) return;
    const unsubDefs = onSnapshot(
      query(collection(db, 'badge_definitions'), orderBy('createdAt', 'asc')),
      snap => setDefinitions(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.isActive)),
      () => {}
    );
    const unsubEarned = onSnapshot(
      collection(db, 'user_badges', userId, 'earned'),
      snap => {
        const map = {};
        snap.docs.forEach(d => { map[d.id] = d.data(); });
        setEarned(map);
      },
      () => {}
    );
    return () => { unsubDefs(); unsubEarned(); };
  }, [userId]);

  useEffect(() => {
    if (tab !== 'leaderboard') return;
    const db = DB();
    if (!db) return;
    setLbLoading(true);

    Promise.all([
      getDocs(collectionGroup(db, 'earned')),
      getDocs(query(collection(db, 'authorized_users'))),
    ]).then(([earnedSnap, membersSnap]) => {
      const memberMap = {};
      membersSnap.docs.forEach(d => {
        const m = d.data();
        memberMap[d.id] = { name: m.name || m.email || d.id, role: m.role, active: m.active };
      });

      const counts = {};
      const rarestMap = {};
      const recentMap = {};

      earnedSnap.docs.forEach(d => {
        const uid = d.ref.parent.parent?.id;
        if (!uid) return;
        const data = d.data();
        counts[uid] = (counts[uid] || 0) + 1;
        if (!recentMap[uid] || (data.earnedAt?.seconds || 0) > (recentMap[uid]?.seconds || 0)) {
          recentMap[uid] = data.earnedAt;
        }
        // track rarest
        const currentRarity = rarestMap[uid]?.rarity || 'Common';
        // Would need def rarity — approximate from badge name via earnedSnap
        // We'll track it by checking definition list after load
      });

      const rows = Object.entries(counts)
        .map(([uid, count]) => ({
          uid,
          name: memberMap[uid]?.name || 'Unknown',
          role: memberMap[uid]?.role || '—',
          count,
          lastBadge: recentMap[uid],
        }))
        .filter(r => memberMap[r.uid]?.active !== false)
        .sort((a, b) => b.count - a.count);

      setLeaderboard(rows);
      setLbLoading(false);
    }).catch(() => setLbLoading(false));
  }, [tab]);

  const earnedDefs = definitions.filter(d => earned[d.id]);
  const lockedDefs = definitions.filter(d => !earned[d.id] && d.criteria?.type !== 'manual' && d.criteria?.type !== 'milestone');

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px 80px', fontFamily: S.sans }}>
      <style>{`
        @keyframes legendaryGlow {
          0%,100% { box-shadow: 0 0 8px #f59e0b44; border-color: #f59e0b88; }
          50% { box-shadow: 0 0 20px #f59e0b88, 0 0 40px #f59e0b22; border-color: #f59e0b; }
        }
        .badge-legendary { animation: legendaryGlow 2s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: S.mono, fontSize: 9, color: S.primary, letterSpacing: 3, marginBottom: 6 }}>
          ACHIEVEMENTS
        </div>
        <div style={{ fontFamily: S.sans, fontSize: 22, fontWeight: 700, color: S.text }}>
          Badges & Milestones
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 10, color: S.dim, marginTop: 4 }}>
          {earnedDefs.length} of {definitions.length} badges earned
        </div>
      </div>

      {/* Tab strip */}
      <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${S.border}`, marginBottom: 24 }}>
        {[['mine', 'MY BADGES'], ['leaderboard', 'LEADERBOARD']].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ fontFamily: S.mono, fontSize: 9, padding: '8px 20px', background: 'transparent',
              border: 'none', cursor: 'pointer', letterSpacing: 1.5,
              borderBottom: `2px solid ${tab === v ? S.primary : 'transparent'}`,
              color: tab === v ? S.primary : S.dim, transition: 'color 0.15s' }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'mine' && (
        <div>
          {/* Earned */}
          {earnedDefs.length > 0 && (
            <>
              <div style={{ fontFamily: S.mono, fontSize: 9, color: S.muted, letterSpacing: 2, marginBottom: 14 }}>
                EARNED — {earnedDefs.length}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 32 }}>
                {earnedDefs.map(def => {
                  const e = earned[def.id];
                  const rc = RARITY[def.rarity] || S.dim;
                  const isLeg = def.rarity === 'Legendary';
                  return (
                    <div key={def.id}
                      className={isLeg ? 'badge-legendary' : ''}
                      onClick={() => setTooltip(tooltip?.id === def.id ? null : { ...def, earnedData: e })}
                      style={{
                        background: S.panel, border: `1px solid ${rc}55`, cursor: 'pointer',
                        padding: '18px 14px', textAlign: 'center', position: 'relative',
                        transition: 'transform 0.15s', userSelect: 'none',
                      }}
                      onMouseEnter={e2 => e2.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={e2 => e2.currentTarget.style.transform = ''}
                    >
                      <div style={{ fontSize: 36, marginBottom: 10, lineHeight: 1 }}>{def.icon}</div>
                      <div style={{ fontFamily: S.sans, fontSize: 12, fontWeight: 600, color: S.text, marginBottom: 6 }}>
                        {def.name}
                      </div>
                      <div style={{ fontFamily: S.mono, fontSize: 8, color: rc, letterSpacing: 1.5,
                        background: `${rc}15`, padding: '2px 8px', display: 'inline-block', marginBottom: 8 }}>
                        {def.rarity.toUpperCase()}
                      </div>
                      <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>
                        {relTime(e?.earnedAt)}
                      </div>
                      {tooltip?.id === def.id && (
                        <div style={{ position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
                          background: '#1e2a43', border: `1px solid ${S.border}`, padding: '10px 14px',
                          width: 200, zIndex: 100, textAlign: 'left' }}>
                          <div style={{ fontFamily: S.sans, fontSize: 11, color: S.text, marginBottom: 4 }}>{def.description}</div>
                          <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>Earned {relTime(e?.earnedAt)}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Locked */}
          {lockedDefs.length > 0 && (
            <>
              <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: 2, marginBottom: 14 }}>
                LOCKED — {lockedDefs.length}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
                {lockedDefs.map(def => {
                  const rc = RARITY[def.rarity] || S.dim;
                  return (
                    <div key={def.id} style={{ background: S.panel, border: `1px solid ${S.border}`,
                      padding: '18px 14px', textAlign: 'center', opacity: 0.45, position: 'relative' }}>
                      <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 12 }}>🔒</div>
                      <div style={{ fontSize: 36, marginBottom: 10, lineHeight: 1, filter: 'grayscale(1)' }}>{def.icon}</div>
                      <div style={{ fontFamily: S.sans, fontSize: 12, fontWeight: 600, color: S.muted, marginBottom: 6 }}>
                        {def.name}
                      </div>
                      <div style={{ fontFamily: S.mono, fontSize: 8, color: rc, letterSpacing: 1.5,
                        background: `${rc}10`, padding: '2px 8px', display: 'inline-block', marginBottom: 8 }}>
                        {def.rarity.toUpperCase()}
                      </div>
                      <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, lineHeight: 1.4 }}>
                        {def.description}
                      </div>
                      {def.criteria?.threshold && (
                        <div style={{ fontFamily: S.mono, fontSize: 8, color: S.dim, marginTop: 6 }}>
                          Goal: {def.criteria.threshold} {def.criteria.type?.replace('_', ' ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {earnedDefs.length === 0 && lockedDefs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', fontFamily: S.mono, fontSize: 11, color: S.dim }}>
              No badge definitions yet. Ask an admin to seed the default badges.
            </div>
          )}
        </div>
      )}

      {tab === 'leaderboard' && (
        <div>
          {lbLoading && (
            <div style={{ textAlign: 'center', padding: 48, fontFamily: S.mono, fontSize: 11, color: S.dim }}>
              Loading leaderboard…
            </div>
          )}
          {!lbLoading && leaderboard.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, fontFamily: S.mono, fontSize: 11, color: S.dim }}>
              No badges earned yet.
            </div>
          )}
          {!lbLoading && leaderboard.length > 0 && (
            <div style={{ background: S.panel, border: `1px solid ${S.border}` }}>
              <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 120px 160px 140px',
                padding: '10px 16px', borderBottom: `1px solid ${S.border}`,
                fontFamily: S.mono, fontSize: 8, color: S.dim, letterSpacing: 1.5 }}>
                <span>RANK</span><span>MEMBER</span><span>BADGES</span><span>LAST EARNED</span><span>PORTAL</span>
              </div>
              {leaderboard.slice(0, 20).map((row, i) => (
                <div key={row.uid}
                  style={{ display: 'grid', gridTemplateColumns: '44px 1fr 120px 160px 140px',
                    padding: '10px 16px', borderBottom: `1px solid ${S.faint}`,
                    background: row.uid === userId ? `${S.primary}08` : 'transparent',
                    alignItems: 'center' }}>
                  <span style={{ fontFamily: S.mono, fontSize: 13, color: i === 0 ? '#f59e0b' : i === 1 ? '#d1d5db' : i === 2 ? '#cd7c3a' : S.dim }}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </span>
                  <span style={{ fontFamily: S.sans, fontSize: 13, color: S.text, fontWeight: row.uid === userId ? 700 : 400 }}>
                    {row.name}{row.uid === userId && <span style={{ fontFamily: S.mono, fontSize: 8, color: S.primary, marginLeft: 8 }}>YOU</span>}
                  </span>
                  <span style={{ fontFamily: S.mono, fontSize: 13, color: S.primary }}>{row.count}</span>
                  <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>{relTime(row.lastBadge)}</span>
                  <span style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>{row.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
